import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ImageUpload from "../ImageUpload";
import { Sparkles, Loader2, Plus, Pencil, Copy, Trash2, Archive, PauseCircle, PlayCircle, Send, Info } from "lucide-react";
import { toast } from "sonner";

const STATUS_STYLE = {
  draft: { label: "Bozza", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
  in_review: { label: "In revisione", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  published: { label: "Pubblicato", cls: "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30" },
  suspended: { label: "Sospeso", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  archived: { label: "Archiviato", cls: "bg-zinc-700/30 text-zinc-500 border-zinc-600/30" },
};

const EMPTY = {
  title: "", description: "", category: "servizio", occasions: [], images: [],
  duration_min: 60, pricing_mode: "fixed", price: "", min_participants: 1, max_participants: 2,
  location_mode: "at_provider", zone: "", address: "", flow_mode: "contact_request",
  availability_mode: "always", availability_days: [], availability_from: "09:00", availability_to: "18:00",
  slot_duration_min: 60, min_advance_hours: 0, cancellation_policy: "", inclusions: "", exclusions: "",
  requirements: "", experience_optin: true,
};

export default function ServiceOfferingsPanel() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/provider/service-offerings");
    setItems(data.offerings || []);
    setArchivedCount(data.archived_count || 0);
    setLoading(false);
  };
  useEffect(() => {
    api.get("/provider/service-offerings/meta").then(({ data }) => setMeta(data));
    load();
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm((f) => ({ ...f, [k]: f[k].includes(v) ? f[k].filter((x) => x !== v) : [...f[k], v] }));

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (o) => {
    setEditId(o.id);
    setForm({ ...EMPTY, ...o, price: o.price ?? "", images: o.images || [], occasions: o.occasions || [], availability_days: o.availability_days || [] });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { ...form, price: form.pricing_mode === "quote" ? null : (form.price === "" ? null : parseFloat(form.price)),
        duration_min: parseInt(form.duration_min) || null, min_participants: parseInt(form.min_participants) || null,
        max_participants: parseInt(form.max_participants) || null, slot_duration_min: parseInt(form.slot_duration_min) || 60,
        min_advance_hours: parseInt(form.min_advance_hours) || 0 };
      if (editId) await api.put(`/provider/service-offerings/${editId}`, payload);
      else await api.post("/provider/service-offerings", payload);
      toast.success(editId ? "Servizio aggiornato" : "Servizio creato (bozza)");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const submit = (o) => act(() => api.post(`/provider/service-offerings/${o.id}/submit`), "Inviato in revisione");
  const dup = (o) => act(() => api.post(`/provider/service-offerings/${o.id}/duplicate`), "Duplicato");
  const suspend = (o, v) => act(() => api.patch(`/provider/service-offerings/${o.id}/suspend`, { suspended: v }), v ? "Sospeso" : "Riattivato");
  const archive = (o) => act(() => api.post(`/provider/service-offerings/${o.id}/archive`, { archived: true }), "Archiviato");
  const del = (o) => { if (!window.confirm("Eliminare la bozza?")) return; act(() => api.delete(`/provider/service-offerings/${o.id}`), "Eliminato"); };

  if (loading || !meta) return <div className="text-zinc-500 py-10 text-center"><Loader2 className="h-4 w-4 animate-spin inline" /> Caricamento…</div>;

  return (
    <div className="space-y-5" data-testid="service-offerings-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-[#7DF9FF]/15 border border-[#7DF9FF]/30 flex items-center justify-center"><Sparkles className="h-5 w-5 text-[#7DF9FF]" /></div>
          <div><h2 className="font-display text-xl font-bold">Servizi ed Esperienze</h2><p className="text-sm text-zinc-400">Crea e gestisci i tuoi servizi. Diventano pubblici dopo l'approvazione della Centrale.</p></div>
        </div>
        <Button data-testid="svc-new-btn" onClick={openNew} className="bg-[#00E676] text-black rounded-full"><Plus className="h-4 w-4 mr-1" /> Nuovo servizio</Button>
      </div>

      <div className="rounded-lg border border-[#7DF9FF]/20 bg-[#7DF9FF]/5 px-4 py-2.5 text-xs text-zinc-300 flex items-start gap-2">
        <Info className="h-4 w-4 text-[#7DF9FF] shrink-0 mt-0.5" />
        Un servizio nuovo parte come <b>bozza</b>. Invialo in revisione: la Centrale lo approva e diventa disponibile nelle Esperienze AI. Modificando prezzo, categoria o luogo di un servizio pubblicato, tornerà in revisione.
      </div>

      {items.length === 0 && <div className="text-zinc-500 py-14 text-center border border-dashed border-white/10 rounded-xl">Nessun servizio. Crea il primo!</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map((o) => {
          const st = STATUS_STYLE[o.status] || STATUS_STYLE.draft;
          return (
            <div key={o.id} data-testid={`svc-card-${o.id}`} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4 flex flex-col gap-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate">{o.title}</div>
                  <div className="text-xs text-zinc-500">{o.category} · {o.pricing_mode === "quote" ? "Preventivo" : `€${(o.price || 0).toFixed(2)}${o.pricing_mode === "from" ? "+" : ""}`}</div>
                </div>
                <span className={`text-[10px] rounded-full px-2 py-0.5 border shrink-0 ${st.cls}`} data-testid={`svc-status-${o.id}`}>{st.label}</span>
              </div>
              {o.review_note && <div className="text-xs text-amber-400/90 bg-amber-500/10 rounded-md px-2 py-1">Nota: {o.review_note}</div>}
              <div className="flex flex-wrap gap-1.5 mt-auto pt-1">
                <button data-testid={`svc-edit-${o.id}`} onClick={() => openEdit(o)} className="text-xs flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5 hover:bg-white/10"><Pencil className="h-3 w-3" /> Modifica</button>
                {o.status === "draft" && <button data-testid={`svc-submit-${o.id}`} onClick={() => submit(o)} className="text-xs flex items-center gap-1 rounded-md bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 px-2.5 py-1.5"><Send className="h-3 w-3" /> Invia in revisione</button>}
                {o.status === "published" && <button data-testid={`svc-suspend-${o.id}`} onClick={() => suspend(o, true)} className="text-xs flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5"><PauseCircle className="h-3 w-3" /> Sospendi</button>}
                {o.status === "suspended" && <button data-testid={`svc-reactivate-${o.id}`} onClick={() => suspend(o, false)} className="text-xs flex items-center gap-1 rounded-md bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30 px-2.5 py-1.5"><PlayCircle className="h-3 w-3" /> Riattiva</button>}
                <button data-testid={`svc-dup-${o.id}`} onClick={() => dup(o)} className="text-xs flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5"><Copy className="h-3 w-3" /> Duplica</button>
                {o.status === "draft" ? (
                  <button data-testid={`svc-del-${o.id}`} onClick={() => del(o)} className="text-xs flex items-center gap-1 rounded-md bg-red-500/10 text-red-400 border border-red-500/20 px-2.5 py-1.5"><Trash2 className="h-3 w-3" /> Elimina</button>
                ) : (
                  <button data-testid={`svc-archive-${o.id}`} onClick={() => archive(o)} className="text-xs flex items-center gap-1 rounded-md bg-white/5 border border-white/10 px-2.5 py-1.5"><Archive className="h-3 w-3" /> Archivia</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {archivedCount > 0 && <p className="text-xs text-zinc-600">{archivedCount} servizio/i archiviato/i.</p>}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#1C1C1F] border-white/10 max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="svc-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifica servizio" : "Nuovo servizio"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Titolo *</Label><Input data-testid="svc-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="svc-desc" value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Categoria</Label>
                <select data-testid="svc-category" value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm mt-1">
                  {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
                </select>
              </div>
              <div><Label>Modalità flusso</Label>
                <select data-testid="svc-flow" value={form.flow_mode} onChange={(e) => set("flow_mode", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm mt-1">
                  {meta.flow_modes.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>
            </div>

            <div><Label>Occasioni compatibili</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {["romantica", "compleanno", "serata_casa", "amici", "piove", "budget", "sorprendimi"].map((o) => (
                  <button key={o} type="button" data-testid={`svc-occ-${o}`} onClick={() => toggleArr("occasions", o)}
                    className={`text-xs rounded-full px-3 py-1.5 border ${form.occasions.includes(o) ? "bg-[#00E676]/15 text-[#00E676] border-[#00E676]/40" : "bg-white/5 text-zinc-300 border-white/15"}`}>{o}</button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div><Label>Prezzo</Label>
                <select data-testid="svc-pricing" value={form.pricing_mode} onChange={(e) => set("pricing_mode", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm mt-1">
                  {meta.pricing_modes.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
              </div>
              {form.pricing_mode !== "quote" && <div><Label>€</Label><Input data-testid="svc-price" type="number" value={form.price} onChange={(e) => set("price", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>}
              <div><Label>Durata (min)</Label><Input data-testid="svc-duration" type="number" value={form.duration_min} onChange={(e) => set("duration_min", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Partecipanti min</Label><Input data-testid="svc-min-part" type="number" value={form.min_participants} onChange={(e) => set("min_participants", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
              <div><Label>Partecipanti max</Label><Input data-testid="svc-max-part" type="number" value={form.max_participants} onChange={(e) => set("max_participants", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Luogo</Label>
                <select data-testid="svc-location" value={form.location_mode} onChange={(e) => set("location_mode", e.target.value)} className="w-full bg-[#0A0A0C] border border-white/10 rounded-md px-3 py-2 text-sm mt-1">
                  {meta.location_modes.map((l) => <option key={l.key} value={l.key}>{l.label}</option>)}
                </select>
              </div>
              <div><Label>Comune</Label><Input data-testid="svc-zone" value={form.zone} onChange={(e) => set("zone", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" /></div>
            </div>

            <div><Label>Immagini</Label><div className="mt-1"><ImageUpload testid="svc-image" folder="services" value={form.images[0] || ""} onChange={(url) => set("images", url ? [url] : [])} /></div></div>

            <div className="bg-[#0A0A0C] border border-white/10 rounded-lg p-3 space-y-3">
              <div className="flex gap-2">
                {[["always", "Sempre disponibile"], ["scheduled", "Fasce orarie"]].map(([m, l]) => (
                  <button key={m} type="button" data-testid={`svc-avail-${m}`} onClick={() => set("availability_mode", m)}
                    className={`text-sm rounded-full px-4 py-1.5 border ${form.availability_mode === m ? "bg-[#00E676] text-black border-[#00E676]" : "bg-white/5 text-zinc-300 border-white/15"}`}>{l}</button>
                ))}
              </div>
              {form.availability_mode === "scheduled" && (
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-1.5">
                    {meta.weekdays.map((w) => (
                      <button key={w.key} type="button" data-testid={`svc-day-${w.key}`} onClick={() => toggleArr("availability_days", w.key)}
                        className={`text-xs rounded-lg px-3 py-1.5 border ${form.availability_days.includes(w.key) ? "bg-[#7DF9FF]/15 text-[#7DF9FF] border-[#7DF9FF]/40" : "bg-white/5 text-zinc-300 border-white/15"}`}>{w.label}</button>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-zinc-400">Dalle</span>
                    <Input data-testid="svc-from" type="time" value={form.availability_from} onChange={(e) => set("availability_from", e.target.value)} className="bg-[#1C1C1F] border-white/10 w-32" />
                    <span className="text-zinc-400">alle</span>
                    <Input data-testid="svc-to" type="time" value={form.availability_to} onChange={(e) => set("availability_to", e.target.value)} className="bg-[#1C1C1F] border-white/10 w-32" />
                  </div>
                  {form.flow_mode === "slot_booking" && (
                    <div className="flex items-center gap-2 text-sm">
                      <span className="text-zinc-400">Durata slot (min)</span>
                      <Input data-testid="svc-slot-dur" type="number" value={form.slot_duration_min} onChange={(e) => set("slot_duration_min", e.target.value)} className="bg-[#1C1C1F] border-white/10 w-24" />
                    </div>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-zinc-400">Anticipo minimo prenotazione (ore)</span>
                <Input data-testid="svc-advance" type="number" value={form.min_advance_hours} onChange={(e) => set("min_advance_hours", e.target.value)} className="bg-[#1C1C1F] border-white/10 w-24" />
              </div>
            </div>

            <div><Label>Inclusioni</Label><Textarea data-testid="svc-inclusions" value={form.inclusions} onChange={(e) => set("inclusions", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>
            <div><Label>Esclusioni</Label><Textarea data-testid="svc-exclusions" value={form.exclusions} onChange={(e) => set("exclusions", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>
            <div><Label>Requisiti / limitazioni</Label><Textarea data-testid="svc-requirements" value={form.requirements} onChange={(e) => set("requirements", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>
            <div><Label>Regole di cancellazione</Label><Textarea data-testid="svc-cancel" value={form.cancellation_policy} onChange={(e) => set("cancellation_policy", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" rows={2} /></div>

            <div className="flex items-center justify-between bg-[#0A0A0C] border border-white/10 rounded-lg px-4 py-3">
              <div><div className="text-sm font-semibold">Disponibile nelle Esperienze AI</div><div className="text-xs text-zinc-500">L'AI Concierge potrà proporre questo servizio.</div></div>
              <Switch data-testid="svc-optin" checked={form.experience_optin} onCheckedChange={(v) => set("experience_optin", v)} />
            </div>

            <Button data-testid="svc-save" onClick={save} disabled={busy} className="bg-[#00E676] text-black rounded-full w-full">{busy ? "…" : (editId ? "Salva modifiche" : "Crea servizio")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
